import Handlebars from 'handlebars';
import _, { cloneDeep } from 'lodash';
import { Preference } from 'sr-types/lib/account/v1/graphql';
import { PdfConfigInput } from 'sr-types/lib/reporting/v1/graphql';
import { failedToastMessage, successToastMessage } from '../../common/utils/commonUtils';
import download from '../../common/utils/download';
import { GROUP_BY_FIELD_NAMES, SORT_WORKSPACE_NAME } from '../components/forms/groupByConfigurations/groupByConstants';
import { AKA, GENERAL_KEY_VALUES, PUNCTUATION_MARKS_CONSTANTS, SORT_DIRECTIONS } from '../constants/constants';
import {
    ENTITIES,
    getCustomSortRankOrderFieldNames,
    getCustomSortRankOrderFields,
    type Entity
} from '../constants/customSortFieldConfigs';
import { DEFAULT_PAGE_CONFIG, REPORT_CONST } from '../constants/reportConstants';
import { REGEX_FOR_HTML } from '../lexical/lexicaljsEditorUtils';
import { toDisplayLocaleDateAndTime } from '../utils/dateTime';
import './handlebarsHelpers';
import { makeReportTemplatesPreferenceKey } from './reportPreferenceUtils';
import type {
    GroupOption,
    RankOrderObjectValue,
    RankOrderValue,
    ReportConfigurations,
    TemplatePreference
} from './reportTypes';
const SHOW_ATTRIBUTE_RUNTIME = 'runtime';

type KeyValue = { key?: string; value?: any };
type NameValue = { name?: string; value?: any };
const ALL_OTHERS_GROUP_KEY = 'ALL OTHERS';
const DEFAULT_CUSTOM_SORT_ENTITY = ENTITIES.workspace;

const makeEmptyRankOrderConfig = (entity: Entity): Record<string, RankOrderValue[]> => {
    const rankOrderFieldNames = getCustomSortRankOrderFieldNames(entity);
    return Object.fromEntries(rankOrderFieldNames.map((name) => [name, []]));
};

const extractRankOrderConfig = (
    source: Record<string, any> | undefined,
    entity: Entity
): Record<string, RankOrderValue[]> => {
    const rankOrderFieldNames = getCustomSortRankOrderFieldNames(entity);
    return Object.fromEntries(rankOrderFieldNames.map((name) => [name, selectRankOrderArray(source, [name])]));
};

const resolveRankOrderConfigWithFallback = (
    source: Record<string, any> | undefined,
    fallback: ReportConfigurations,
    entity: Entity
): Record<string, RankOrderValue[]> => {
    const rankOrderFieldNames = getCustomSortRankOrderFieldNames(entity);
    return Object.fromEntries(
        rankOrderFieldNames.map((name) => [
            name,
            (source?.[name] as RankOrderValue[] | undefined) ?? (fallback[name] as RankOrderValue[] | undefined) ?? []
        ])
    );
};

const normalizeRankOrderConfig = (config: ReportConfigurations, entity: Entity): Record<string, string[]> => {
    const rankOrderFieldNames = getCustomSortRankOrderFieldNames(entity);
    return Object.fromEntries(
        rankOrderFieldNames.map((name) => [
            name,
            normalizeRankOrderLabels(config[name] as RankOrderValue[] | undefined)
        ])
    );
};

/**
 * Recursively searches through a JSON structure to find the table row template.
 * Prefers the first non-header row when a header row exists; otherwise falls back to the first row.
 */
export function findRowTemplate(json) {
    const isHeaderRow = (row) => {
        if (!Array.isArray(row?.children)) {
            return false;
        }

        const tableCells = row.children.filter((cell) => cell?.type === 'tablecell');
        return (
            tableCells.length > 0 &&
            tableCells.every((cell) => typeof cell.headerState === 'number' && cell.headerState !== 0)
        );
    };

    function search(nodes) {
        for (const n of nodes) {
            if (n.type === 'table') {
                const tableRows = Array.isArray(n.children)
                    ? n.children.filter((child) => child?.type === 'tablerow')
                    : [];

                if (tableRows.length > 0) {
                    return tableRows.find((row) => !isHeaderRow(row)) || tableRows[0];
                }
            }

            if (n.children) {
                const found = search(n.children);
                if (found) return found;
            }
        }
        return undefined;
    }
    return search(json.root?.children || []);
}

/**
 * Recursively fills Handlebars placeholders in a node tree with actual data from rowData.
 * Converts HTML results to richtext nodes and removes styling from empty results.
 */
export function fillRowPlaceholders(node, rowData) {
    if (!node || !rowData) return;

    if (node.text && typeof node.text === 'string') {
        const compiled = Handlebars.compile(node.text);
        const result = compiled({ ...rowData, reportDate: new Date().toLocaleDateString() });
        node.text = result;

        if (result && REGEX_FOR_HTML.test(result)) {
            // if the result is HTML, set type as richtext (details on SHOW-6315)
            node.type = 'richtext';
        }

        if (!result) {
            node.style = '';
        }
    }

    if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child) => fillRowPlaceholders(child, rowData));
    }
}

export function generateGroupKey(
    sortOrder: (string | number)[],
    reportConfigurations: ReportConfigurations,
    entity: Entity = DEFAULT_CUSTOM_SORT_ENTITY
): string {
    const workspaceRankOrderFields = getCustomSortRankOrderFields(entity);
    const groupByFields = Array.isArray(reportConfigurations.workspaceGroupBy)
        ? reportConfigurations.workspaceGroupBy
              .map((option) => (typeof option === 'string' ? option : option?.id))
              .filter(Boolean)
        : [];

    if (groupByFields.length === 0) {
        return '';
    }

    const getRankOrderArray = (field: string): string[] => {
        const rankOrderKey = workspaceRankOrderFields[field];
        if (rankOrderKey && Array.isArray(reportConfigurations[rankOrderKey])) {
            return normalizeRankOrderLabels(reportConfigurations[rankOrderKey]).map((v) => v.toUpperCase());
        }
        return [];
    };

    // Check the first groupBy field in order to create "All Others" group
    const firstGrpField = groupByFields[0];
    const firstVal = sortOrder[0];
    const firstTrimmed = normalizeGroupKeyValue(firstVal);

    const firstGrpFieldRankOrder = getRankOrderArray(firstGrpField);

    if (!firstTrimmed || !firstGrpFieldRankOrder.includes(firstTrimmed.toUpperCase())) {
        return ALL_OTHERS_GROUP_KEY;
    }

    // Build the group key, only for valid values in their rank order arrays
    const groupKey = groupByFields
        .map((field, index) => {
            const val = sortOrder[index];
            const trimmedVal = normalizeGroupKeyValue(val);
            const rankOrderArray = getRankOrderArray(field);
            const upperTrimmedVal = trimmedVal.toUpperCase();
            return trimmedVal && rankOrderArray.includes(upperTrimmedVal) ? upperTrimmedVal : '';
        })
        .filter(Boolean)
        .join(' ' + PUNCTUATION_MARKS_CONSTANTS.pipe + ' ');

    return groupKey.length > 0 ? groupKey : '';
}

/**
 * Groups an array of objects using their resolved sort order values.
 * @param dataRows Array of objects to group.
 * @param sortOrders Resolved sort order values for each row.
 * @param reportConfigurations Report configurations for rank order mappings.
 * @param pickFields Optional: fields to include in grouped output (default: full row).
 * @returns the grouped map.
 */
export function groupRowsUsingSortOrders<T extends Record<string, any>>(
    dataRows: T[],
    sortOrders: (string | number)[][],
    reportConfigurations: ReportConfigurations,
    pickFields?: string[],
    entity: Entity = DEFAULT_CUSTOM_SORT_ENTITY
): Record<string, T[]> {
    const grouped: Record<string, T[]> = {};

    dataRows.forEach((row, index) => {
        const sortOrder = sortOrders[index] || [];
        const key = generateGroupKey(sortOrder, reportConfigurations, entity);
        const item = pickFields ? (Object.fromEntries(pickFields.map((field) => [field, row[field]])) as T) : row;

        if (!grouped[key]) {
            grouped[key] = [];
        }

        grouped[key].push(item);
    });

    return grouped;
}

/**
 * Transforms a report template into a final document by filling it with data rows.
 * Supports two strategies - 'tableRows' with grouped (with section headers) and ungrouped layouts.
 * and 'documentContext' for filling placeholders in a document context.
 */
export function resolveTemplate(
    selectedTemplate: string | Record<string, any>,
    data: Record<string, any>[] | Record<string, any> = [],
    locale: string,
    strategy: 'tableRows' | 'documentContext' = 'tableRows',
    entity: Entity = DEFAULT_CUSTOM_SORT_ENTITY,
    reportConfigurations?: ReportConfigurations,
    sortOrders?: (string | number)[][]
): string {
    const now = new Date();
    const formattedReportDate = toDisplayLocaleDateAndTime(now, true, locale);
    const isString = typeof selectedTemplate === 'string';
    let templateStr = isString ? selectedTemplate : JSON.stringify(selectedTemplate);
    templateStr = templateStr.replace(/{{reportDate}}/g, formattedReportDate);
    const templParsed = JSON.parse(templateStr);
    const templResolved = cloneDeep(templParsed);

    if (strategy === 'documentContext') {
        const context = _.isPlainObject(data) ? (data as Record<string, any>) : {};
        const rootNode = templResolved?.root ?? templResolved;
        fillRowPlaceholders(rootNode, context);
        return JSON.stringify(templResolved);
    }

    const dataRows = Array.isArray(data) ? data : [];

    // Find the table row template
    const rowTemplate = findRowTemplate(templParsed);
    if (!rowTemplate) return JSON.stringify(templResolved);

    // CASE: Grouping data on select fields
    if (reportConfigurations?.workspaceGroupBy?.length && Array.isArray(sortOrders) && sortOrders.length > 0) {
        return resolveTemplateWithGroups({
            templParsed,
            templResolved,
            rowTemplate,
            dataRows,
            sortOrders,
            reportConfigurations,
            entity
        });
    } else {
        // CASE Ungrouped: find the table node and fill rows
        return resolveTemplateWithNoGroups({
            templResolved,
            rowTemplate,
            dataRows
        });
    }
}

/**
 * Resolves a template with grouped data, creating separate table sections with headers for each group.
 * Each group gets its own heading and table populated with rows matching that group.
 */
function resolveTemplateWithGroups({
    templParsed,
    templResolved,
    rowTemplate,
    dataRows,
    sortOrders,
    reportConfigurations,
    entity
}) {
    // Marking the row template
    rowTemplate.isTemplateRow = true;
    // Remove any existing tables from the template for grouping
    templResolved.root.children = templResolved.root.children.filter((child) => child.type !== 'table');
    const sortTokens = buildSortTokens(reportConfigurations.workspaceGroupBy || [], reportConfigurations, entity);
    const resolvedSortOrders = Array.isArray(sortOrders) ? resolveSortOrders(sortTokens, sortOrders) : [];

    // Group data rows by their resolved sort order values
    const groupedRows: Record<string, any[]> = groupRowsUsingSortOrders(
        dataRows,
        resolvedSortOrders,
        reportConfigurations,
        undefined,
        entity
    );

    Object.entries(groupedRows).forEach(([key, rows]) => {
        templResolved.root.children.push({
            type: 'heading',
            tag: 'h2',
            children: [
                {
                    type: 'text',
                    text: key,
                    version: 1
                }
            ],
            direction: null,
            format: 'center',
            indent: 0,
            version: 1,
            textStyle: 'font-weight: bold; font-size: 18px;'
        });

        // Table for this group
        const tableNode = templParsed.root.children.find((child) => child.type === 'table');
        if (tableNode) {
            const newTable = cloneDeep(tableNode);
            rows.forEach((rowData) => {
                const clonedRow = cloneDeep(rowTemplate);
                delete clonedRow.isTemplateRow; // Remove marker before filling
                fillRowPlaceholders(clonedRow, rowData);
                newTable.children.push(clonedRow);
            });
            // Remove the template row after filling in data
            newTable.children = newTable.children.filter((child) => !child.isTemplateRow);
            templResolved.root.children.push(newTable);
        }
    });
    return JSON.stringify(templResolved);
}

/**
 * Resolves a template without grouping, simply populating the table with all data rows sequentially.
 * Removes the template row and replaces it with cloned rows filled with actual data.
 */
function resolveTemplateWithNoGroups({ templResolved, rowTemplate, dataRows }) {
    const tableNode = (() => {
        function search(nodes) {
            for (const n of nodes) {
                if (n.type === 'table' && n.children?.some((child) => _.isEqual(child, rowTemplate))) {
                    return n;
                }
                if (n.children) {
                    const found = search(n.children);
                    if (found) return found;
                }
            }
            return undefined;
        }
        return search(templResolved.root?.children || []);
    })();

    if (tableNode) {
        tableNode.children = tableNode.children.filter((child) => !_.isEqual(child, rowTemplate));
        for (const rowData of dataRows) {
            const clonedRow = cloneDeep(rowTemplate);
            fillRowPlaceholders(clonedRow, rowData);
            tableNode.children.push(clonedRow);
        }
    }
    return JSON.stringify(templResolved);
}

type HandleDownloadPDFParams = {
    generateReportFn: GenerateReportFn;
    activeOrganizationAccount: string;
    reportTitle: string;
    content: string;
    pageConfig: PdfConfigInput;
    contextTerm?: string;
};

type GenerateReportFn = (
    reportTitle: string,
    content: string,
    pageConfig: PdfConfigInput,
    contextTerm?: string
) => Promise<any>;

/*  This makes an API call generateReport to production form app, which generates the PDF using the inputs
    Post receiving the asset id of the PDF, it downloads the PDF using the download util
    Params:
        generateReportFn: function to call the API
        activeOrganizationAccount: object
        content: html string
        reportTitle: string
        pageConfig: PageConfig
        contextTerm: string to identify the context of the report
*/
export const handleDownloadPDF = async ({
    generateReportFn,
    activeOrganizationAccount,
    reportTitle,
    content,
    pageConfig,
    contextTerm
}: HandleDownloadPDFParams) => {
    try {
        const res = await generateReportFn(reportTitle, content, pageConfig, contextTerm);
        const reportId = res.data?.generateReport?.id;
        const filename = res.data?.generateReport?.reference?.label;
        download(reportId, filename, activeOrganizationAccount);
        successToastMessage('report.message.download.success');
    } catch (err) {
        failedToastMessage('report.message.download.failure', err.message);
    }
};

/**
 * Prepare template for design/placeholder preview:
 * - Replace reportTitle
 * - Keep the row template intact
 *
 * templateJson: object
 * reportTitle: string
 */

export function prepareTemplateWithPlaceholders(selectedTemplate, reportTitle = '') {
    let templateStr = JSON.stringify(selectedTemplate?.template || selectedTemplate);
    templateStr = templateStr.replace(/{{reportTitle}}/g, reportTitle);
    return templateStr;
}

/**
 * Retrieves a report template from preferences by its ID.
 * Parses and returns the template object, or null if not found or invalid.
 */
export function getTemplateById(id?: string, preferences?: any[]) {
    if (!id || !Array.isArray(preferences)) return null;

    const pref = preferences.find((p) => p.identity?.id === id || p.id === id);

    if (!pref) return null;

    try {
        const parsed = typeof pref.value === 'string' ? JSON.parse(pref.value) : pref.value;
        return parsed;
    } catch (e) {
        console.warn('Failed to parse template JSON:', e, pref);
        return null;
    }
}

/**
 * Converts an array of key-value pairs into a flat object for report-template context.
 *
 * AKA values retain the historical `AKA <title>` prefix by default. Set `includeAkaPrefix` to
 * false only when a template needs the raw alternative title. Runtime values are also formatted
 * from minutes into their display representation.
 */
export function keyValueArrayToObject(
    arr: KeyValue[],
    { includeAkaPrefix = true }: { includeAkaPrefix?: boolean } = {}
): Record<string, any> {
    if (!Array.isArray(arr)) return {};
    return arr.reduce(
        (obj, { key, value }) => {
            if (!key) return obj;
            if (key === AKA && includeAkaPrefix) {
                obj[key] = `${GENERAL_KEY_VALUES.aka} ${value}`;
            } else if (key.toLowerCase() === SHOW_ATTRIBUTE_RUNTIME) {
                obj[key] = formatRuntimeFromMinutes(value);
            } else {
                obj[key] = value;
            }
            return obj;
        },
        {} as Record<string, any>
    );
}

/**
 * Converts an array of name-value pairs into a flat object.
 * Similar to keyValueArrayToObject but uses 'name' property instead of 'key'.
 */
export function nameValueArrayToObject(arr: NameValue[]): Record<string, any> {
    if (!Array.isArray(arr)) return {};
    return arr.reduce(
        (obj, { name, value }) => {
            if (!name) return obj;
            obj[name] = value;
            return obj;
        },
        {} as Record<string, any>
    );
}

/**
 * Minifies a JSON value by removing whitespace.
 * Handles both string and object inputs, returning a compact JSON string.
 * ---> NOT USED ANYMORE <---
 */
export const minifyJson = (value: string | object) => {
    if (typeof value === 'string') {
        try {
            return JSON.stringify(JSON.parse(value));
        } catch (e) {
            return value.trim();
        }
    }
    try {
        return JSON.stringify(value);
    } catch (e) {
        return String(value);
    }
};

/**
 * Converts user preferences into an array of report template objects.
 * Filters for report template preferences and parses their JSON values into structured template data.
 */
export function mapPreferencesToTemplates(preferences?: Preference[], preferenceEntity?: string): TemplatePreference[] {
    if (!preferences?.length || !preferenceEntity?.trim()) return [];

    const preferenceKey = makeReportTemplatesPreferenceKey(preferenceEntity);

    return preferences
        .filter((p) => typeof p.value === 'string' && p.value.length > 0 && p.key?.startsWith(preferenceKey))
        .map((p): TemplatePreference => {
            let parsed: unknown = null;
            try {
                parsed = JSON.parse(p.value);
            } catch (e) {
                console.warn('Invalid template JSON:', e, p);
            }

            return {
                id: (p as Preference & { id?: string }).id ?? '',
                name: p.name ?? '',
                description: p.summary?.about ?? '',
                type: REPORT_CONST,
                template: parsed,
                createdByName: (p as Preference & { createdByName?: string }).createdByName ?? '',
                updatedByName: (p as Preference & { updatedByName?: string }).updatedByName ?? '',
                createdOn: (p as Preference & { createdOn?: string }).createdOn ?? '',
                updatedOn: (p as Preference & { updatedOn?: string }).updatedOn ?? '',
                __raw: p
            };
        });
}

/**
 * Extracts the ID from a workspace group option, handling both string and object forms.
 */
export const getOptionId = (option: GroupOption) => (typeof option === 'string' ? option : option?.id);

/**
 * Compares two arrays of workspace group options to determine if they contain the same selections in the same order.
 */
export const areSelectionsEqual = (a: GroupOption[] = [], b: GroupOption[] = []) => {
    if (a.length !== b.length) {
        return false;
    }
    return a.every((option, index) => getOptionId(option) === getOptionId(b[index]));
};

/**
 * Checks if a rank order value has meaningful content (non-empty array, non-blank string, or truthy value).
 */
export const hasRankOrderValues = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (typeof value === 'string') {
        return value.trim().length > 0;
    }
    return Boolean(value);
};

/**
 * Returns a fresh instance of default report configurations with empty grouping and rank order arrays.
 */
export const getDefaultReportConfigurations = (entity: Entity = DEFAULT_CUSTOM_SORT_ENTITY): ReportConfigurations => ({
    sortWorkspacesBy: '',
    workspaceGroupBy: [],
    ...makeEmptyRankOrderConfig(entity),
    sortCohortsBy: 'publictitle_ASC',
    pageConfig: DEFAULT_PAGE_CONFIG
});

/**
 * Searches for and returns the first rank order array found among a list of possible key names in the source object.
 */
const selectRankOrderArray = (source: Record<string, any> | undefined, keys: string[]): RankOrderValue[] => {
    if (!source) {
        return [];
    }

    for (const key of keys) {
        if (Array.isArray(source[key])) {
            return source[key] as RankOrderValue[];
        }
    }

    return [];
};

/**
 * Extracts the workspace group-by field from a source object, checking multiple possible property names.
 */
const pickWorkspaceGroupField = (source?: Record<string, any>): GroupOption[] => {
    if (!source) {
        return [];
    }

    if (Array.isArray(source[GROUP_BY_FIELD_NAMES.workspaceGroupBy])) {
        return source[GROUP_BY_FIELD_NAMES.workspaceGroupBy];
    }

    if (Array.isArray(source.workspaceGroupBy)) {
        return source.workspaceGroupBy;
    }

    return [];
};

/**
 * Returns a complete PDFConfig object, filling in any missing attributes with defaults.
 */
export function getPageConfig(source?: Record<string, any>): PdfConfigInput {
    const config: PdfConfigInput = {
        pageLayout: source?.pageLayout || source?.pageConfig?.pageLayout || DEFAULT_PAGE_CONFIG.pageLayout,
        pageSize: source?.pageSize || source?.pageConfig?.pageSize || DEFAULT_PAGE_CONFIG.pageSize,
        scale: source?.scale || source?.pageConfig?.scale || DEFAULT_PAGE_CONFIG.scale
    };
    return config;
}

/**
 * Extracts all report configurations from a source object, including workspace grouping, rank orders, and layout.
 * Returns default values if source is undefined or fields are missing.
 */
export const extractReportConfigurations = (
    source?: Record<string, any>,
    entity: Entity = DEFAULT_CUSTOM_SORT_ENTITY
): ReportConfigurations => {
    if (!source) {
        return getDefaultReportConfigurations(entity);
    }
    const workspaceGroupBy = pickWorkspaceGroupField(source);
    return {
        // Determining Sort, if not saved (old existing templates) defaulting to 'custom' if there were fields selected for sort (rank ordering)
        sortWorkspacesBy: source.sortWorkspacesBy || (workspaceGroupBy.length !== 0 ? 'custom' : ''),
        workspaceGroupBy,
        ...extractRankOrderConfig(source, entity),
        // Determining Cohort sort, if not saved (old existing templates) defaulting to 'publictitle_ASC' in case of custom sort
        sortCohortsBy: source.sortCohortsBy || (workspaceGroupBy.length !== 0 ? 'publictitle_ASC' : ''),
        pageConfig: getPageConfig(source)
    };
};

/**
 * Merges source values with fallback configurations, ensuring all required report configuration fields are present.
 * Source values take precedence, with fallback used for missing fields.
 */
export const resolveReportConfigureValues = (
    source: Record<string, any> | undefined,
    fallback: ReportConfigurations,
    entity: Entity = DEFAULT_CUSTOM_SORT_ENTITY
): Record<string, any> => {
    const rankOrderConfig = resolveRankOrderConfigWithFallback(source, fallback, entity);

    return {
        ...fallback,
        ...source,
        [SORT_WORKSPACE_NAME.sortWorkspacesBy]:
            source?.[SORT_WORKSPACE_NAME.sortWorkspacesBy] ?? fallback.sortWorkspacesBy ?? '',
        [SORT_WORKSPACE_NAME.sortCohortsBy]:
            source?.[SORT_WORKSPACE_NAME.sortCohortsBy] ?? fallback.sortCohortsBy ?? '',
        [GROUP_BY_FIELD_NAMES.workspaceGroupBy]:
            source?.[GROUP_BY_FIELD_NAMES.workspaceGroupBy] ?? fallback.workspaceGroupBy ?? [],
        ...rankOrderConfig,
        [GROUP_BY_FIELD_NAMES.pageLayout]:
            source?.[GROUP_BY_FIELD_NAMES.pageLayout] ??
            fallback.pageConfig?.pageLayout ??
            DEFAULT_PAGE_CONFIG.pageLayout,
        [GROUP_BY_FIELD_NAMES.pageSize]:
            source?.[GROUP_BY_FIELD_NAMES.pageSize] ?? fallback.pageConfig?.pageSize ?? DEFAULT_PAGE_CONFIG.pageSize,
        [GROUP_BY_FIELD_NAMES.scale]:
            source?.[GROUP_BY_FIELD_NAMES.scale] ?? fallback.pageConfig?.scale ?? DEFAULT_PAGE_CONFIG.scale
    };
};

/**
 * Converts an array of workspace group option objects into an array of their IDs.
 */
export const mapWorkspaceGroupSelectionsToIds = (selections: GroupOption[] = []): string[] => {
    return selections.map((selection) => getOptionId(selection)).filter((id): id is string => Boolean(id));
};

/**
 * Converts an array of workspace group IDs back into full option objects by matching against available options.
 */
export const mapWorkspaceGroupIdsToSelections = (
    ids: (string | undefined)[] = [],
    options: GroupOption[] = []
): GroupOption[] => {
    return ids
        .map((id) => (id ? options.find((option) => getOptionId(option) === id) : undefined))
        .filter((option): option is GroupOption => Boolean(option));
};

/**
 * Constructs a template configuration payload from report configuration, normalizing all rank order values to label arrays.
 */
export const buildTemplateConfigPayload = (
    config: ReportConfigurations,
    entity: Entity = DEFAULT_CUSTOM_SORT_ENTITY
) => {
    const rankOrderConfig = normalizeRankOrderConfig(config, entity);

    return {
        sortDataBy: config.sortWorkspacesBy,
        groupDataBy: mapWorkspaceGroupSelectionsToIds(config.workspaceGroupBy),
        ...rankOrderConfig,
        sortCohortsBy: config.sortCohortsBy,
        pageConfig: { ...config.pageConfig, scale: Number(config.pageConfig?.scale) || DEFAULT_PAGE_CONFIG.scale }
    };
};

/**
 * Converts stored template configuration back into ReportConfigurations format.
 * Maps workspace IDs to full option objects using the provided options list.
 */
export const mapTemplateConfigToReportConfigurations = (
    templateConfig: Record<string, any> | undefined,
    workspaceOptions: GroupOption[] = [],
    entity: Entity
): ReportConfigurations => {
    if (!templateConfig) {
        return getDefaultReportConfigurations(entity);
    }

    const sortWorkspacesBy = templateConfig.sortDataBy || '';
    const workspaceGroupBy = mapWorkspaceGroupIdsToSelections(
        Array.isArray(templateConfig.groupDataBy) ? templateConfig.groupDataBy : [],
        workspaceOptions
    );
    const rankOrderConfig = extractRankOrderConfig(templateConfig, entity);

    return extractReportConfigurations(
        {
            sortWorkspacesBy,
            workspaceGroupBy,
            ...rankOrderConfig,
            sortCohortsBy: templateConfig.sortCohortsBy,
            pageConfig: templateConfig.pageConfig
        },
        entity
    );
};

type WorkspaceGroupSelection = { sortId?: string; id?: string };

/**
 * Extracts the sort ID from group option, checking sortId property first, then falling back to id.
 */
const getSortId = (selection: GroupOption): string | undefined => {
    if (typeof selection === 'string') {
        return selection;
    }

    const typedSelection = selection as WorkspaceGroupSelection;
    return typedSelection.sortId || typedSelection.id;
};

/**
 * Extracts label strings from rank order values, handling both string and object forms.
 * Returns an array of trimmed, non-empty label strings.
 */
export const normalizeRankOrderLabels = (values?: RankOrderValue[]): string[] => {
    if (!Array.isArray(values)) {
        return [];
    }

    return values
        .map((rawValue) => {
            if (typeof rawValue === 'string') {
                return rawValue.trim();
            }

            if (rawValue && typeof rawValue === 'object') {
                const typedValue = rawValue as RankOrderObjectValue;
                const label = typedValue.label || typedValue.value || typedValue.name;
                return typeof label === 'string' ? label.trim() : undefined;
            }

            return undefined;
        })
        .filter((label): label is string => Boolean(label));
};

/**
 * Encodes a sort key with optional rank order labels into a formatted sort token string.
 * Format: sortKey:asc;label1:1;label2:2;...
 */
const encodeRankedSortToken = (sortKey: string, rankLabels?: string[]): string => {
    const baseToken = `${sortKey}${SORT_DIRECTIONS.asc}`;

    if (!rankLabels?.length) {
        return baseToken;
    }

    const serializedRanks = rankLabels
        .map(
            (label, index) =>
                `${label}${PUNCTUATION_MARKS_CONSTANTS.colon}${index + 1}${PUNCTUATION_MARKS_CONSTANTS.semicolon}`
        )
        .join('');

    return `${baseToken}${PUNCTUATION_MARKS_CONSTANTS.semicolon}${serializedRanks}`;
};

/**
 * Builds an array of sort tokens from group selections and their associated rank order configurations.
 * Each token encodes the sort field and its custom ranking order for API consumption.
 */
export const buildSortTokens = (
    selections: GroupOption[],
    reportConfiguration?: ReportConfigurations,
    entity: Entity = DEFAULT_CUSTOM_SORT_ENTITY
): string[] => {
    if (!Array.isArray(selections) || selections.length === 0) {
        return [];
    }

    const rankOrderFields = getCustomSortRankOrderFields(entity);
    return selections
        .map((selection) => {
            const sortKey = getSortId(selection);
            if (!sortKey) {
                return undefined;
            }

            const optionId = getOptionId(selection);
            const rankFieldKey = optionId ? rankOrderFields[optionId] : undefined;
            const rankValues = rankFieldKey && reportConfiguration ? reportConfiguration[rankFieldKey] : undefined;
            const rankLabels = normalizeRankOrderLabels(rankValues);

            return encodeRankedSortToken(sortKey, rankLabels);
        })
        .filter((token): token is string => Boolean(token));
};

/**
 * Normalizes a value for group key comparison:
 * - Converts to string if not already
 * - Trims whitespace
 * - Returns '' if value is null, undefined, or a dash
 */
function normalizeGroupKeyValue(val: any): string {
    if (val === null || val === undefined) return '';
    const str = String(val).trim();
    return str === PUNCTUATION_MARKS_CONSTANTS.dash ? '' : str;
}

/**
 * Resolves sort order values into their corresponding labels based on report
 * configurations, for use in grouping and display.
 */
function resolveSortOrders(workspaceSortTokens: string[], sortOrders: (string | number)[][]): (string | number)[][] {
    if (!workspaceSortTokens.length) {
        return sortOrders;
    }

    const lookupMaps = workspaceSortTokens.map((fieldConfig) => {
        const parts = fieldConfig.split(PUNCTUATION_MARKS_CONSTANTS.semicolon);

        if (parts.length <= 1) return null;

        const map: Record<string, string> = {};
        const orderSegments = parts.slice(1).filter(Boolean);

        orderSegments.forEach((segment) => {
            const [label, order] = segment.split(PUNCTUATION_MARKS_CONSTANTS.colon);
            if (!label || !order) {
                return;
            }

            map[order] = label;
        });

        if (Object.keys(map).length === 0) {
            return null;
        }

        return map;
    });

    return sortOrders.map((row) =>
        row.map((value, index) => {
            const currentMap = lookupMaps[index];

            if (!currentMap) return value;
            const stringValue = String(value);
            return Object.prototype.hasOwnProperty.call(currentMap, stringValue)
                ? currentMap[stringValue]
                : ALL_OTHERS_GROUP_KEY;
        })
    );
}

const formatRuntimeFromMinutes = (value: string): string => {
    const trimmedValue = value?.trim() ?? '';
    if (trimmedValue.length === 0) {
        return trimmedValue;
    }

    // Duration-like strings (e.g. "1h", "1h 30m") should pass through unchanged.
    if (!/^\d+$/.test(trimmedValue)) {
        return trimmedValue;
    }

    const totalMinutes = Number(trimmedValue);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
    }

    if (hours > 0) {
        return `${hours}h`;
    }

    return `${minutes}m`;
};
